import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  Typography,
  makeStyles,
  Box,
  Fade
} from '@material-ui/core';
import Page from 'src/components/Page';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import SecurityIcon from '@material-ui/icons/Security';
import BuildIcon from '@material-ui/icons/Build';
import { useHistory } from 'react-router-dom';
import CuteStar from 'src/components/CuteStar/CuteStar.js';
import { useDispatch, useSelector } from 'react-redux';
import { cuteStarActions } from 'src/store/slices/cuteStarSlice';
import { paymentThunks } from 'src/store/slices/paymentSlice'; // NEW: activated tenants call

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 'calc(100vh - 140px)',
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    position: 'relative'
  },
  cardWrap: {
    perspective: 1200
  },
  card: {
    position: 'relative',
    height: 220,
    borderRadius: 24,
    overflow: 'hidden',
    background:
      'linear-gradient(140deg, #1e3c72 0%, #2a5298 35%, #5b86e5 100%)',
    color: '#fff',
    boxShadow: '0 20px 40px -10px rgba(0,0,0,0.35)',
    transform: 'translateY(0) scale(1)',
    transition: 'all 480ms cubic-bezier(.19,1,.22,1)',
    '&:hover': {
      transform: 'translateY(-10px) scale(1.035)',
      boxShadow: '0 35px 60px -15px rgba(0,0,0,0.55)'
    },
    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      width: 220,
      height: 220,
      top: -60,
      right: -60,
      background: 'rgba(255,255,255,0.12)',
      borderRadius: '50%',
      filter: 'blur(2px)',
      transform: 'scale(0)',
      transition: 'transform 0.8s ease'
    },
    '&:hover:before': {
      transform: 'scale(1)'
    },
    '&:hover:after': {
      transform: 'scale(1.4)'
    }
  },
  gradientBlue: {
    background:
      'linear-gradient(135deg,#004e92 0%,#000428 100%)'
  },
  gradientGreen: {
    background:
      'linear-gradient(135deg,#0f9b0f 0%,#1d976c 50%,#93f9b9 100%)'
  },
  gradientOrange: {
    background:
      'linear-gradient(135deg,#d53369 0%,#daae51 100%)'
  },
  title: {
    fontWeight: 600,
    letterSpacing: '.5px',
    color: '#fff' // beyaz
  },
  sub: {
    fontSize: 13,
    lineHeight: 1.55,
    letterSpacing: '.3px',
    color: '#fff', // beyaz
    opacity: 0.95
  },
  caption: {
    color: '#fff',
    opacity: 0.85,
    fontWeight: 500,
    letterSpacing: '.5px'
  },
  iconBox: {
    fontSize: 54,
    opacity: 0.9,
    marginBottom: theme.spacing(2),
    transition: 'transform 0.6s cubic-bezier(.19,1,.22,1)',
    transformOrigin: 'left center',              // eklendi: sağa doğru büyüsün
    '& svg': {
      fontSize: 54
    },
    color: '#fff' // ikon da saf beyaz ton
  },
  cardInner: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    justifyContent: 'space-between'
  },
  actionArea: {
    height: '100%'
  },
  hoverScale: {
    '&:hover $iconBox': {
      transform: 'translate(10px,-6px) scale(1.08)' // sağa kay + büyüme
    }
  },
  star: {
    position: 'absolute',
    top: 10,
    right: 12,
    zIndex: 2,
    pointerEvents: 'none',
    opacity: 0.95
  },
  disabledCard: {
    filter: 'grayscale(0.55)',
    opacity: 0.55,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  }
}));

const items = [
  {
    key: 'product',
    title: 'Product Sales',
    desc: 'Start product sales processes.',
    icon: <ShoppingCartIcon />,
    gradient: 'gradientBlue',
    target: '/Sales/Product'
  },
  {
    key: 'material',
    title: 'Material Sales',
    desc: 'Part / material sales operation.',
    icon: <BuildIcon />,
    gradient: 'gradientOrange',
    target: '/Sales/Material'
  },
  {
    key: 'warranty',
    title: 'Extra Warranty Sales',
    desc: 'Extended warranty offer and activation area.',
    icon: <SecurityIcon />,
    gradient: 'gradientGreen',
    target: '/Sales/ExtraWarranty'
  }
];

// items: key -> SalesTypeId mapping: product:1, material:2, warranty:3

const Sales = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const activatedTenants = useSelector(state => state.payment.activatedTenants);

  React.useEffect(() => {
    dispatch(cuteStarActions.resetPush());
    dispatch(cuteStarActions.updateStar({
      expression: 'happy',
      message: 'Hello, I am Stella, your sales assistant. Which sale would you like to start today?',
      showBubble: true
    }));
    // Fetch active Sironex sales types
    dispatch(paymentThunks.GetSironexActivatedTenants());
  }, [dispatch]);

  const handleSelect = (item) => {
    history.push(item.target);
  };

  // Determine active sales types
  const activeTypeIds = new Set(
    (activatedTenants || [])
      .filter(x => x && x.Active)
      .map(x => x.SalesTypeId)
  );

  const mappedItems = items.map(it => {
    const salesTypeMap = { product: 1, material: 2, warranty: 3 };
    const typeId = salesTypeMap[it.key];
    const isActive = activeTypeIds.has(typeId);
    return { ...it, isActive };
  });

  return (
    <Page>
      <Container maxWidth="xl">
        <Fade in timeout={600}>
          <div className={classes.root}>
            <CuteStar/>
            <Grid container spacing={4} direction="column">
              {mappedItems.map(it => (
                <Grid item xs={12} key={it.key} className={classes.cardWrap}>
                  <Card
                    className={`sales-card ${classes.card} ${classes[it.gradient]} ${classes.hoverScale} ${!it.isActive ? classes.disabledCard : ''}`}
                  >
                    <CardActionArea
                      className={classes.actionArea}
                      onClick={it.isActive ? (() => handleSelect(it)) : undefined}
                    >
                      <div className={classes.cardInner}>
                        <Box display="flex" flexDirection="column">
                          <div className={classes.iconBox}>{it.icon}</div>
                          <Typography variant="h5" className={classes.title}>
                            {it.title}
                          </Typography>
                          <Typography className={classes.sub}>
                            {it.desc}
                          </Typography>
                        </Box>
                        <Typography variant="caption" className={classes.caption}>
                          {it.isActive ? 'Click to continue →' : 'Inactive sales type'}
                        </Typography>
                      </div>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        </Fade>
      </Container>
    </Page>
  );
};
export default Sales;